/**
 * slice-3952 (#3780 / #3785 / #3799 / #3800 / #3803 / #3823): the "data changed" signal.
 *
 * PB re-retrieves a list when the operator comes back to it from a New / maintenance sheet
 * (the list window's `ue_retrieve` runs on activate / after the child closes), so a record
 * created or deleted elsewhere is in — or gone from — the grid on return. PH10's browse grid
 * keeps its rows for the 30-minute retention window (slice-3572: TanStack `staleTime` + the
 * `sessionStorage` retained seed) so Back from a pure drill is instant; nothing told it that a
 * write had happened in between, so the grid showed the old rows until Refresh.
 *
 * {@link api} calls {@link noteDataWrite} after every SUCCESSFUL non-GET request. Listeners
 * (installed once by `installStaleOnDataWrite`) mark the cached browse lists stale WITHOUT
 * refetching them, so the next mount of a list re-reads it once — no refetch storm, and a Back
 * with no write in between still does not re-hit the database.
 *
 * Writes that change no warehouse data (screen-usage / testers-hub telemetry, auth, client
 * error reports, per-user UI settings, grid profiles, record locks) are ignored: they fire on
 * plain navigation and would otherwise defeat slice-3572's Back retention.
 */

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Path prefixes whose writes never change the rows a list shows. */
const NON_DATA_PREFIXES = [
  '/api/auth/',
  '/api/screen-usage/',
  '/api/testers-hub/',
  '/api/feedback',
  '/api/client-errors',
  '/api/users/settings',
  '/api/profiles/',
  '/api/locks/',
  '/api/languages/session-language',
  // ADR-0067 log-only would-403 sink (telemetry; the sender uses raw fetch, this is belt and braces).
  '/api/security/',
];

/** Pessimistic edit-lock take/release on open/close of a maintenance sheet (…/lock, …/unlock). */
const NON_DATA_SUFFIX = /\/(un)?lock$/;

type Listener = () => void;
const listeners = new Set<Listener>();
let lastWriteAt = 0;

/** True when a successful request with this method + path may have changed list data. */
export function isDataWrite(method: string | undefined, path: string): boolean {
  if (!WRITE_METHODS.has((method ?? 'GET').toUpperCase())) return false;
  const bare = path.split('?')[0] ?? path;
  if (NON_DATA_PREFIXES.some((p) => bare.startsWith(p))) return false;
  if (NON_DATA_SUFFIX.test(bare)) return false;
  return true;
}

/** Called by the API client after a successful request. */
export function noteDataWrite(method: string | undefined, path: string): void {
  if (!isDataWrite(method, path)) return;
  lastWriteAt = Date.now();
  for (const l of [...listeners]) {
    try {
      l();
    } catch {
      /* a broken listener must never fail the request that succeeded */
    }
  }
}

/** Subscribe to data writes. Returns the unsubscribe function. */
export function onDataWrite(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Epoch ms of the last data write in this tab (0 = none yet). */
export function lastDataWriteAt(): number {
  return lastWriteAt;
}
